import React, { useState, useEffect } from "react";
import { api } from "../api";
import "./BudgetEditor.css";
import "./SalaryView.css";

const months = [
  "April", "May", "June", "July", "August", "September",
  "October", "November", "December", "January", "February", "March",
];

const monthAbbr: Record<string, string> = {
  April: "Apr", May: "May", June: "Jun", July: "Jul", August: "Aug",
  September: "Sep", October: "Oct", November: "Nov", December: "Dec",
  January: "Jan", February: "Feb", March: "Mar",
};

// Same reference logic as BudgetEditor
// Headcount types auto-computed from salary employees (shown as locked)
const COMPUTED_HC_TYPES = new Set(["FULLTIME", "PARTTIME", "WORKCOMP"]);

const dimensionReference: Record<string, string> = {
  "Annual Budget":    "Annual Budget",
  "Actuals":          "Actuals",
  "July Forecast":    "Annual Budget",
  "October Forecast": "July Forecast",
  "January Forecast": "October Forecast",
};

interface HeadcountMonthlyValue {
  month: string;
  headcount_value: number;
}

interface HeadcountGroup {
  headcount_type: string;
  monthly_values: HeadcountMonthlyValue[];
}

interface HeadcountEditorProps {
  costCenterNumber: string;
  isAdmin?: boolean;
}

const HeadcountEditor: React.FC<HeadcountEditorProps> = ({ costCenterNumber, isAdmin }) => {
  const [dimension, setDimension] = useState<string | null>(null);
  const [headcountData, setHeadcountData] = useState<HeadcountGroup[]>([]);
  const [referenceData, setReferenceData] = useState<HeadcountGroup[]>([]);
  const [annualHeadcountData, setAnnualHeadcountData] = useState<HeadcountGroup[]>([]);
  const [forecastActive, setForecastActive] = useState(false);
  const [forecastDim, setForecastDim] = useState<string | null>(null);

  useEffect(() => {
    api.get("/settings/forecast-config", { params: { cc: costCenterNumber } })
      .then(res => {
        setForecastActive(res.data.active as boolean);
        const dim = (res.data.dimension || null) as string | null;
        setForecastDim(dim);
        setDimension(dim || "Annual Budget");
      })
      .catch(() => { setDimension("Annual Budget"); });
  }, []);

  const isDimOpen = (dim: string): boolean => {
    if (isAdmin) return true;
    if (dim === "Annual Budget") return true;
    return forecastActive && dim === forecastDim;
  };

  const dimWindowTitle = (): string =>
    forecastActive ? "Not the active forecast cycle" : "Forecast planning is currently disabled";

  useEffect(() => {
    if (!costCenterNumber || !dimension) { setHeadcountData([]); return; }
    api.get(`/headcount`, { params: { cost_center: costCenterNumber, dimension } })
      .then((res) => setHeadcountData(res.data as HeadcountGroup[]))
      .catch((err) => console.error("Failed to fetch headcount data:", err));
  }, [costCenterNumber, dimension]);

  useEffect(() => {
    if (!costCenterNumber || !dimension) { setReferenceData([]); return; }
    const refDim = dimensionReference[dimension] ?? "Annual Budget";
    api.get(`/headcount`, { params: { cost_center: costCenterNumber, dimension: refDim } })
      .then((res) => setReferenceData(res.data as HeadcountGroup[]))
      .catch((err) => console.error("Failed to fetch reference headcount data:", err));
  }, [costCenterNumber, dimension]);

  useEffect(() => {
    if (!costCenterNumber) { setAnnualHeadcountData([]); return; }
    api.get(`/headcount`, { params: { cost_center: costCenterNumber, dimension: "Annual Budget" } })
      .then((res) => setAnnualHeadcountData(res.data as HeadcountGroup[]))
      .catch((err) => console.error("Failed to fetch Annual Budget headcount:", err));
  }, [costCenterNumber]);

  const formatNumber = (value: number) =>
    new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(value));

  const formatVariance = (value: number): string => {
    const r = Math.round(value);
    return r < 0 ? `(${Math.abs(r)})` : r.toString();
  };

  // Effective headcount: for forecast dimensions, inherit Annual Budget values
  // for any headcount type not present in the forecast data
  const effectiveData: HeadcountGroup[] = (() => {
    if (dimension === "Annual Budget" || annualHeadcountData.length === 0) return headcountData;
    // Start with all forecast data
    const merged = [...headcountData];
    const existingTypes = new Set(headcountData.map(g => g.headcount_type));
    // Add any Annual Budget types not present in forecast
    for (const annualGroup of annualHeadcountData) {
      if (!existingTypes.has(annualGroup.headcount_type)) {
        merged.push(annualGroup);
      }
    }
    // If forecast had zero data entirely, use all Annual Budget values
    if (headcountData.length === 0) return annualHeadcountData;
    return merged;
  })();

  const annualTotal = annualHeadcountData.reduce(
    (sum, g) => sum + g.monthly_values.reduce((s, mv) => s + mv.headcount_value, 0), 0
  );
  const dimTotal = effectiveData.reduce(
    (sum, g) => sum + g.monthly_values.reduce((s, mv) => s + mv.headcount_value, 0), 0
  );

  const allTypes = Array.from(new Set([
    ...effectiveData.map(g => g.headcount_type),
    ...referenceData.map(g => g.headcount_type),
  ]));

  const footerDimByMonth = months.map(m =>
    effectiveData.reduce((sum, g) => sum + (g.monthly_values.find(mv => mv.month === m)?.headcount_value || 0), 0)
  );
  const footerRefByMonth = months.map(m =>
    referenceData.reduce((sum, g) => sum + (g.monthly_values.find(mv => mv.month === m)?.headcount_value || 0), 0)
  );
  const footerVarByMonth = footerRefByMonth.map((ref, i) => ref - footerDimByMonth[i]);
  const footerDimTotal = footerDimByMonth.reduce((a, b) => a + b, 0);
  const footerVarTotal = footerVarByMonth.reduce((a, b) => a + b, 0);

  const dimLabel = dimension === "Actuals" ? "Actuals"
    : dimension === "Annual Budget" ? "Annual"
    : dimension?.replace(" Forecast", "") ?? "—";

  const showVarianceRow = dimension !== "Annual Budget";
  const refDimLabel = dimension ? (dimensionReference[dimension] ?? "Annual Budget") : "—";
  const refLabel = refDimLabel === "Annual Budget" ? "Annual"
    : refDimLabel === "Actuals" ? "Actuals"
    : refDimLabel.replace(" Forecast", "");

  const refTotal = referenceData.reduce(
    (sum, g) => sum + g.monthly_values.reduce((s, mv) => s + mv.headcount_value, 0), 0
  );

  return (
    <div className="budget-container">

      {/* ── KPI Strip — unified row ────────────────────────────────────── */}
      <div className="kpi-strip" style={{ display: "flex", alignItems: "stretch", justifyContent: "space-between", gap: 14 }}>
        {/* Left: Budget / Forecast totals */}
        <div style={{ display: "flex", alignItems: "stretch", gap: 14 }}>
          <div className="kpi-card kpi-card--neutral">
            <span className="kpi-label">Annual Budget</span>
            <span className="kpi-value">{formatNumber(annualTotal)}</span>
            <span className="kpi-note">MM Capacity</span>
          </div>

          {dimension !== "Annual Budget" && (<>
            {refDimLabel !== "Annual Budget" && (
              <div className="kpi-card kpi-card--teal">
                <span className="kpi-label">{refDimLabel}</span>
                <span className="kpi-value">{formatNumber(refTotal)}</span>
                <span className="kpi-note">Reference Forecast</span>
              </div>
            )}
            <div className="kpi-card kpi-card--current">
              <span className="kpi-current-header">
                <span className="kpi-current-name">{dimension}</span>
                <span className="kpi-current-tag">Current</span>
              </span>
              <span className="kpi-value kpi-value--current">{formatNumber(dimTotal)}</span>
              <span className="kpi-note kpi-note--current">Current Forecast</span>
            </div>
          </>)}
        </div>

        {/* Right: Variance & Growth */}
        {dimension !== "Annual Budget" && (() => {
          const refLabel2 = refDimLabel === "Annual Budget" ? "Annual Budget" : `${refLabel} Forecast`;
          const varVal    = dimTotal - refTotal;
          const growthPct = refTotal > 0 ? (varVal / refTotal) * 100 : 0;
          const isOver    = varVal > 0;
          return (
            <div style={{ display: "flex", alignItems: "stretch", gap: 14 }}>
              <div className={`budget-header-kpi ${isOver ? "budget-header-kpi--bad" : "budget-header-kpi--good"}`}>
                <span className="budget-header-kpi-label">HC Variance vs {refLabel2}</span>
                <span className="budget-header-kpi-value">
                  {varVal >= 0 ? "+" : ""}{formatNumber(varVal)}
                </span>
                <span className="budget-header-kpi-note"><span style={{ color: "#16a34a" }}>&#9679;</span> Favorable &nbsp;·&nbsp; <span style={{ color: "#dc2626" }}>&#9679;</span> Unfavorable</span>
              </div>
              <div className={`budget-header-kpi ${isOver ? "budget-header-kpi--bad" : "budget-header-kpi--good"}`}>
                <span className="budget-header-kpi-label">% Growth vs {refLabel2}</span>
                <span className="budget-header-kpi-value">
                  {growthPct >= 0 ? "+" : ""}{growthPct.toFixed(1)}%
                </span>
                <span className="budget-header-kpi-note"><span style={{ color: "#16a34a" }}>&#9679;</span> Favorable &nbsp;·&nbsp; <span style={{ color: "#dc2626" }}>&#9679;</span> Unfavorable</span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      <div className="filters">
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "13px", fontWeight: 700, color: "#374151", whiteSpace: "nowrap" }}>Forecast Cycle:</span>
          <div className="dim-switcher">
            {[
              { dim: "Annual Budget",    label: "Annual",  color: "#475569" },
              { dim: "July Forecast",    label: "July",    color: "#4A90E2" },
              { dim: "October Forecast", label: "October", color: "#ea580c" },
              { dim: "January Forecast", label: "January", color: "#059669" },
            ].map(({ dim, label, color }) => {
              const isActive = dimension === dim;
              const open = isDimOpen(dim);
              return (
                <button
                  key={dim}
                  className={`dim-pill${isActive ? " dim-pill--active" : ""}${!open ? " dim-pill--disabled" : ""}`}
                  style={isActive
                    ? { background: color, boxShadow: `0 3px 10px ${color}55` }
                    : { color: open ? "#374151" : "#9ca3af" }}
                  onClick={() => open && setDimension(dim)}
                  title={open ? dim : dimWindowTitle()}
                  disabled={!open}
                >
                  {label}
                  {forecastDim === dim && <span className={`dim-pill-dot${forecastActive ? " dim-pill-dot--live" : ""}`} />}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ flex: 1 }} />
      </div>

      {/* ── Headcount Table ───────────────────────────────────────────────── */}
      <div className="budget-table-wrapper">
        <table className="budget-table">
          <thead>
            <tr>
              <th className="account-col" style={{ minWidth: 90 }}>Type</th>
              <th className="row-type-col">Dim</th>
              {months.map((m) => <th key={m}>{monthAbbr[m]}</th>)}
              <th>Total MM</th>
            </tr>
          </thead>
          <tbody>
            {allTypes.map((hcType) => {
              const dimGroup = effectiveData.find(g => g.headcount_type === hcType);
              const refGroup = referenceData.find(g => g.headcount_type === hcType);
              const dimRowTotal = dimGroup?.monthly_values.reduce((sum, mv) => sum + mv.headcount_value, 0) ?? 0;
              const isComputed = COMPUTED_HC_TYPES.has(hcType);

              return (
                <React.Fragment key={hcType}>
                  <tr className={`forecast-row${isComputed ? " locked-auto-row" : ""}`}>
                    <td
                      rowSpan={showVarianceRow ? 2 : 1}
                      className="account-col"
                      style={{ fontSize: "11px", fontWeight: 600 }}
                    >
                      {hcType}
                      {isComputed && (
                        <span className="material-icons" style={{ fontSize: 11, marginLeft: 4, color: "oklch(0.55 0.02 250)", verticalAlign: "middle" }}>lock</span>
                      )}
                    </td>
                    <td className="row-type-col">
                      <span className={`row-badge ${isComputed ? "row-badge--auto" : "row-badge--forecast"}`}>{dimLabel}</span>
                    </td>
                    {months.map((monthName) => {
                      const mv = dimGroup?.monthly_values.find(v => v.month === monthName);
                      return (
                        <td key={`${hcType}-dim-${monthName}`} style={{ textAlign: "center", fontSize: 13, color: isComputed ? "oklch(0.35 0.02 250)" : "#374151" }}>
                          {formatNumber(mv?.headcount_value ?? 0)}
                        </td>
                      );
                    })}
                    <td className="total-cell">{formatNumber(dimRowTotal)}</td>
                  </tr>

                  {showVarianceRow && (
                    <tr className={`variance-row${isComputed ? " locked-auto-row" : ""}`}>
                      <td className="row-type-col">
                        <span className="row-badge row-badge--variance">Var vs {refLabel}</span>
                      </td>
                      {months.map((monthName) => {
                        const dimVal = dimGroup?.monthly_values.find(v => v.month === monthName)?.headcount_value ?? 0;
                        const refVal = refGroup?.monthly_values.find(v => v.month === monthName)?.headcount_value ?? 0;
                        const variance = refVal - dimVal;
                        return (
                          <td key={`${hcType}-var-${monthName}`} className={variance >= 0 ? "var-positive" : "var-negative"} style={{ textAlign: "center" }}>
                            {formatVariance(variance)}
                          </td>
                        );
                      })}
                      {(() => {
                        const refRowTotal = refGroup?.monthly_values.reduce((sum, mv) => sum + mv.headcount_value, 0) ?? 0;
                        const varTotal = refRowTotal - dimRowTotal;
                        return (
                          <td className={`total-cell ${varTotal >= 0 ? "var-positive" : "var-negative"}`}>
                            {formatVariance(varTotal)}
                          </td>
                        );
                      })()}
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>

          <tfoot>
            <tr className="total-footer-row total-footer-dim">
              <td className="account-col total-footer-label" style={{ minWidth: 90 }}>Total Manmonths</td>
              <td className="row-type-col">
                <span className="row-badge row-badge--forecast">{dimLabel}</span>
              </td>
              {footerDimByMonth.map((val, i) => (
                <td key={`ftd-${months[i]}`} className="total-cell total-footer-cell" style={{ textAlign: "center" }}>
                  {formatNumber(val)}
                </td>
              ))}
              <td className="total-cell total-footer-cell">{formatNumber(footerDimTotal)}</td>
            </tr>
            {showVarianceRow && (
              <tr className="total-footer-row total-footer-var">
                <td className="account-col total-footer-label" style={{ minWidth: 90 }}>Variance</td>
                <td className="row-type-col">
                  <span className="row-badge row-badge--variance">Variance</span>
                </td>
                {footerVarByMonth.map((val, i) => (
                  <td key={`ftv-${months[i]}`} className={`total-footer-cell ${val >= 0 ? "var-positive" : "var-negative"}`} style={{ textAlign: "center" }}>
                    {formatVariance(val)}
                  </td>
                ))}
                <td className={`total-cell total-footer-cell ${footerVarTotal >= 0 ? "var-positive" : "var-negative"}`}>
                  {formatVariance(footerVarTotal)}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>

      {/* Note */}
      <div className="salary-note">
        <span className="material-icons salary-note-icon">info_outline</span>
        <div className="salary-note-content">
          <p><strong>Headcount values are derived from the Salary tab.</strong> To change headcount, update employee entries in the Salary Planning tab. Forecast dimensions inherit from Annual Budget when no forecast-specific data exists.</p>
        </div>
      </div>
    </div>
  );
};

export default HeadcountEditor;
